import { useState } from 'react';

const categories = [
  {
    label: 'Before You Book',
    faqs: [
      {
        q: "I'm not sure which service to book. What should I choose?",
        a: 'All first visits begin with the 90-minute Stability Signature, which includes a comprehensive Stability\u00a0assessment and initial recalibration treatment. If a client would benefit from an enhancement treatment and the schedule allows, the session can be adjusted in real time during the visit.\nOnce the entry session is completed, you will simply follow the personalized treatment plan developed during your assessment.',
      },
      {
        q: 'What is the difference between the Stability Signature and regular acupuncture?',
        a: 'Regular acupuncture typically focuses on addressing a specific health concern by balancing the body\u2019s meridian system.\n\nThe Stability Signature is a facial-led integrative treatment within the Vionne Stability Method, designed to assess how nervous system load, circulation, tissue tension, and skin health influence visible facial changes. It serves as the foundational first session, creating a personalized regulation plan before follow-up treatments.',
      },
      {
        q: 'What should I do to prepare for my first session?',
        a: '— Arrive well-hydrated and not fasting.\n— A light meal 1–2 hours prior is recommended.\n— Wear comfortable and loose clothing.\n— Avoid intense workouts immediately before or after your session.\n— For services involving facial integration, please arrive with minimal or no makeup to allow accurate structural assessment and optimal botanical application.\n— Inform us of any current medical conditions or pregnancy.',
      },
      {
        q: 'Is there parking near the clinic?',
        a: 'Vionne Clinical Atelier is located on West Broadway. Street parking is available in the area, though we recommend allowing a little extra time, particularly during busy periods. The clinic is also accessible by public transit.',
      },
      {
        q: 'Is the Vionne Stability Method suitable for men?',
        a: 'The Vionne Stability Method is designed for women. The protocols, the space, and the way we work are built around the female body — its particular constitution, its hormonal rhythms, and the patterns unique to it. This focus is intentional, and it shapes everything we do.',
      },
      {
        q: 'Do I need to complete a health questionnaire before my visit?',
        a: 'Yes — and it is worth taking a few minutes with it.\n\n48 hours before you arrive, we will send you a short intake form covering your health history, current medications, and any known sensitivities. We read it before your session, not during it. This means that when you walk in, we are already thinking about you — not collecting information from you.',
      },
    ],
  },
  {
    label: 'Results & Treatment',
    faqs: [
      {
        q: 'How many sessions will I realistically need?',
        a: 'Structural stabilization develops progressively. Many clients begin noticing subtle changes within 2–3 sessions.\n\nFor deeper and more sustained results, we typically recommend a focused series of 5–6 sessions, followed by monthly maintenance.\n\nTreatment frequency is always personalized.',
      },
      {
        q: 'Will I see immediate results?',
        a: 'Some clients notice immediate softening or relaxation. However, long-term structural change and nervous system regulation require consistency.\n\nThis work prioritizes sustainable regulation over temporary enhancement.',
      },
      {
        q: 'How often should I return after the initial series?',
        a: 'Once structural stability is established, most clients transition to monthly maintenance. Frequency may vary depending on stress load and hormonal rhythm.',
      },
      {
        q: 'Can I combine facial treatments with acupuncture?',
        a: 'Yes. The Stability Signature already integrates both. If you book a facial or acupuncture session separately, we can discuss whether integration is appropriate based on your goals.',
      },
    ],
  },
  {
    label: 'Safety & Suitability',
    faqs: [
      {
        q: 'Does acupuncture hurt?',
        a: 'Most clients experience minimal discomfort. Facial acupuncture uses very fine needles and is often described as subtle or barely noticeable. Your comfort level is monitored throughout the session.',
      },
      {
        q: 'Is this a replacement for Botox or fillers?',
        a: 'This method does not aim to replace injectables directly. It addresses structural instability and nervous system regulation, which influence how the face holds tension over time.\n\nSome clients choose this as a natural alternative, while others use it as complementary support.',
      },
      {
        q: 'Is this appropriate if my concern is mainly stress and sleep?',
        a: 'Yes. Facial tension is often a visible reflection of nervous system load. Even if your concern feels internal, the facial-led assessment helps guide regulation.',
      },
      {
        q: 'Is this suitable during pregnancy?',
        a: 'Acupuncture can be safe during pregnancy when appropriately adjusted. Please inform us prior to booking so the session can be tailored accordingly.',
      },
      {
        q: 'Are the products used in treatments vegan, cruelty-free, and natural?',
        a: 'Our botanical formulations are selected with those standards as a baseline. We work with plant-derived, cruelty-free ingredients and avoid unnecessary synthetic compounds. If you have specific sensitivities or ingredient concerns, please note them in your intake form so we can account for them in your protocol.',
      },
    ],
  },
  {
    label: 'Cancellation & Rescheduling',
    faqs: [
      {
        q: 'What is your cancellation and rescheduling policy?',
        a: 'We ask for at least 24 hours\' notice if you need to cancel or reschedule.\n\nEach session at VIONNE is prepared in advance — we review your health file, consider your constitution, and set the space with intention before you arrive. That preparation begins well before you walk through the door.\n\nIf plans change, an early message allows us to offer your time to another client who may be waiting. We understand that life is sometimes unpredictable, and we ask only for a thoughtful heads-up when you can.',
      },
    ],
  },
  {
    label: 'Insurance & Billing',
    faqs: [
      {
        q: 'Is acupuncture covered by insurance?',
        a: 'Most major insurance plans cover acupuncture services. Direct billing is available where applicable.\n\nFacial-only treatments are not typically insurance-covered.',
      },
    ],
  },
];

const ClinicalFAQ = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleCategoryChange = (idx: number) => {
    setActiveCategory(idx);
    setOpenIndex(null);
  };

  const currentFaqs = categories[activeCategory].faqs;

  return (
    <section id="clinical-faq" className="py-10 sm:py-14 px-4 sm:px-6 lg:px-12 bg-warmWhite">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-6 h-[1px] bg-bronze"></div>
          <span className="text-bronze text-[10px] font-sans font-semibold tracking-[0.3em] uppercase">
            FAQ
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16">
          {/* Left: Title + Category Tabs */}
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl sm:text-3xl font-light text-charcoal italic mb-2">
              Common Questions
            </h2>
            <p className="text-[11px] sm:text-[12px] text-textGray font-sans font-light leading-relaxed mb-6 max-w-xs">
              Everything you need to know before your first visit.
            </p>

            {/* Category list */}
            <div className="flex flex-col gap-1">
              {categories.map((cat, idx) => (
                <button
                  key={idx}
                  onClick={() => handleCategoryChange(idx)}
                  className={`text-left px-3 py-2.5 rounded-sm text-[11px] sm:text-[12px] font-sans font-medium tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2.5 ${
                    activeCategory === idx
                      ? 'bg-bronze/10 text-bronze'
                      : 'text-textGray hover:text-charcoal hover:bg-warmGray/10'
                  }`}
                >
                  {activeCategory === idx && (
                    <span className="w-1 h-1 rounded-full bg-bronze flex-shrink-0"></span>
                  )}
                  {activeCategory !== idx && (
                    <span className="w-1 h-1 rounded-full bg-transparent flex-shrink-0"></span>
                  )}
                  {cat.label}
                  <span className="ml-auto text-[10px] opacity-50">{cat.faqs.length}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Accordion */}
          <div className="lg:col-span-3 divide-y divide-warmGray/20">
            {currentFaqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={index}>
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full flex items-start justify-between gap-4 py-3.5 text-left cursor-pointer group"
                  >
                    <span
                      className={`text-[12px] sm:text-[13px] font-sans font-medium leading-relaxed transition-colors duration-200 ${
                        isOpen ? 'text-charcoal' : 'text-charcoal/70 group-hover:text-charcoal'
                      }`}
                    >
                      {faq.q}
                    </span>
                    <div
                      className={`w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full border transition-all duration-300 mt-0.5 ${
                        isOpen
                          ? 'bg-bronze border-bronze text-white rotate-45'
                          : 'border-warmGray/30 text-textGray group-hover:border-bronze group-hover:text-bronze'
                      }`}
                    >
                      <i className="ri-add-line text-[10px]"></i>
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="text-[11px] sm:text-[12px] text-textGray font-sans font-light leading-[1.9] pr-6 space-y-1.5">
                      {faq.a.split('\n').map((line, i) => (
                        <p key={i} className={line === '' ? 'h-1' : ''}>
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClinicalFAQ;
